import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { AIChatSession, AIChatMessageRecord, wyscoutMatchesAPI } from '@/services/api';
import EmptyState from '@/components/ui/EmptyState';
import DelayedLoading from '@/components/ui/DelayedLoading';
import LoadingStatus from '@/components/ui/LoadingStatus';

interface ChatHistoryPageProps {
  embedded?: boolean;
}

const ChatHistoryPage: React.FC<ChatHistoryPageProps> = ({ embedded = false }) => {
  const [sessions, setSessions] = useState<AIChatSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSession, setExpandedSession] = useState<string | null>(null);
  const [messages, setMessages] = useState<AIChatMessageRecord[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await wyscoutMatchesAPI.listMyChatSessions();
      setSessions(result.data || []);
    } catch {
      setError('Failed to load chat sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleToggleSession = async (sessionId: string) => {
    if (expandedSession === sessionId) {
      setExpandedSession(null);
      setMessages([]);
      return;
    }
    setExpandedSession(sessionId);
    setLoadingMessages(true);
    try {
      const result = await wyscoutMatchesAPI.getChatSessionMessages(sessionId);
      setMessages(result.data || []);
    } catch {
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleDelete = async (sessionId: string) => {
    try {
      await wyscoutMatchesAPI.deleteChatSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (expandedSession === sessionId) {
        setExpandedSession(null);
        setMessages([]);
      }
      setDeleteConfirm(null);
    } catch {
      setError('Failed to delete session');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getEntityLink = (session: AIChatSession) => {
    const chatParam = `?chatSession=${session.id}`;
    if (session.entityType === 'match') {
      return `/matches/wyscout/${session.entityId}${chatParam}`;
    }
    return `/players/${session.entityId}${chatParam}`;
  };

  const getEntityLabel = (session: AIChatSession) => {
    if (session.entityLabel) return session.entityLabel;
    return session.entityType === 'match' ? 'Unknown match' : 'Unknown player';
  };

  if (loading) {
    return (
      <div className={embedded ? '' : 'px-4'}>
        {!embedded && <h1 className="mb-6 text-2xl font-bold text-gray-900 dark:text-gray-100">Chat History</h1>}
        <DelayedLoading loading={loading}>
          <LoadingStatus variant="panel" label="Loading chat history" className="py-12" />
        </DelayedLoading>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'px-4'}>
      <div className={`flex items-center ${embedded ? 'justify-end' : 'justify-between'} mb-6`}>
        {!embedded && <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Chat History</h1>}
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {sessions.length} conversation{sessions.length !== 1 ? 's' : ''}
        </span>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {sessions.length === 0 ? (
        <EmptyState
          icon={
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          }
          title="No conversations yet"
          description="Start a chat from any match or player page using the AI Analysis button."
        />
      ) : (
        <div className="space-y-3">
          {sessions.map(session => (
            <div
              key={session.id}
              className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              {/* Session header */}
              <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                onClick={() => handleToggleSession(session.id)}
              >
                {/* Expand/collapse chevron */}
                <svg
                  className={`h-4 w-4 text-gray-400 flex-shrink-0 transform transition-transform ${
                    expandedSession === session.id ? 'rotate-90' : ''
                  }`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>

                {/* Entity type badge */}
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${
                    session.entityType === 'match'
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                  }`}
                >
                  {session.entityType === 'match' ? 'Match' : 'Player'}
                </span>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {session.title}
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {getEntityLabel(session)}
                  </p>
                </div>

                {/* Meta */}
                <div className="flex items-center gap-3 flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">
                  <span>{session.messageCount} msgs</span>
                  <span className="hidden sm:inline">{formatDate(session.lastMessageAt || session.updatedAt)}</span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                  <Link
                    to={getEntityLink(session)}
                    className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                    title={`Go to ${session.entityType}`}
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </Link>
                  {deleteConfirm === session.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(session.id)}
                        className="px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:text-red-800 font-medium"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="px-2 py-1 text-xs text-gray-400 hover:text-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(session.id)}
                      className="p-1.5 text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                      title="Delete session"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Expanded messages */}
              {expandedSession === session.id && (
                <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-800/30">
                  {(() => {
                    if (loadingMessages) return (
                      <DelayedLoading loading={loadingMessages}>
                        <LoadingStatus label="Loading messages" className="justify-center py-4" />
                      </DelayedLoading>
                    );
                    if (messages.length === 0) return <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No messages found</p>;
                    return (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {messages.map(msg => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                              msg.role === 'user'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700'
                            }`}
                          >
                            {msg.role === 'assistant' ? (
                              <div className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                                <ReactMarkdown>{msg.content}</ReactMarkdown>
                              </div>
                            ) : (
                              <p>{msg.content}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    );
                  })()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatHistoryPage;
